import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { AnimatePresence, motion } from "framer-motion";
import { Search, Download, X, Loader2, FileCheck2, Ban, MessageSquareWarning, Send, RefreshCw, ChevronRight, Trash2 } from "lucide-react";
import { STATES } from "@/constants/states";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = ["pending_approval", "more_info", "approved", "label_in_progress", "label_generated",
  "label_sent", "shipped", "delivered", "rejected", "label_failed", "cancelled"];
const STATUS_LABELS = {
  pending_approval: "Pending Approval", more_info: "More Information Needed", approved: "Approved",
  label_in_progress: "Label In Progress", label_generated: "Label Generated", label_sent: "Label Sent",
  shipped: "Shipped", delivered: "Delivered", rejected: "Rejected", label_failed: "Label Failed", cancelled: "Cancelled",
};
const STATUS_STYLE = {
  pending_approval: "bg-brandpink text-white", more_info: "bg-amber-500 text-white", approved: "bg-brandsky text-white",
  label_in_progress: "bg-slate-500 text-white", label_generated: "bg-slate-600 text-white", label_sent: "bg-brandleaf text-white",
  shipped: "bg-brandsky-deep text-white", delivered: "bg-brandleaf-deep text-white", rejected: "bg-slate-700 text-white",
  label_failed: "bg-red-600 text-white", cancelled: "bg-slate-400 text-white",
};

const fmt = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return iso || "";
  }
};

function DetailModal({ rid, token, onClose, refresh, settings }) {
  const [d, setD] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState("");
  const [shipping, setShipping] = useState(null);
  const [confirmApprove, setConfirmApprove] = useState(false);

  const load = useCallback(async () => {
    const res = await fetch(`${API}/admin/label-requests/${rid}`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) {
      toast.error("Could not load request");
      return;
    }
    const data = await res.json();
    setD(data);
    setShipping({
      street: data.street || "", unit: data.unit || "", city: data.city || "",
      state: data.state || "", zip: data.zip || "",
    });
  }, [rid, token]);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (body, tag, successMsg) => {
    setBusy(tag);
    try {
      const res = await fetch(`${API}/admin/label-requests/${rid}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(typeof j.detail === "string" ? j.detail : "Action failed");
      }
      toast.success(successMsg);
      setNote("");
      setConfirmApprove(false);
      await load();
      refresh();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy("");
    }
  };

  const uploadLabel = async (e) => {
    e.preventDefault();
    const file = e.target.elements["up-file"].files[0];
    const tracking = e.target.elements["up-tracking"].value;
    if (!file) {
      toast.error("Choose a PDF first");
      return;
    }
    setBusy("upload");
    try {
      const fd = new FormData();
      fd.append("tracking", tracking);
      fd.append("file", file);
      const res = await fetch(`${API}/admin/label-requests/${rid}/upload-label`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(typeof j.detail === "string" ? j.detail : "Upload failed");
      }
      toast.success("Label stored and emailed to the office");
      await load();
      refresh();
    } catch (e2) {
      toast.error(e2.message);
    } finally {
      setBusy("");
    }
  };

  const generateLabel = async () => {
    setBusy("generate");
    try {
      const res = await fetch(`${API}/admin/label-requests/${rid}/generate-label`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof j.detail === "string" ? j.detail : "Label generation failed");
      toast.success(`Label generated — tracking ${j.tracking}`);
      await load();
      refresh();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy("");
    }
  };

  const download = async (path, name) => {
    const res = await fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) {
      toast.error("Download failed");
      return;
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-start justify-center overflow-y-auto bg-abyss/60 p-4 backdrop-blur-sm sm:p-8" data-testid="label-detail-modal" onClick={onClose}>
      <div className="my-8 w-full max-w-3xl rounded-[24px] bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {!d ? (
          <div className="flex items-center justify-center gap-3 p-16 text-slate-400">
            <Loader2 className="h-5 w-5 animate-spin" /> Loading…
          </div>
        ) : (
          <div className="p-6 sm:p-8">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex flex-wrap items-center gap-2.5">
                  <span className="font-mono rounded-lg bg-ink px-2.5 py-1 text-[11.5px] font-bold text-white">{d.request_id}</span>
                  <span className={`rounded-full px-3 py-1 text-[11px] font-extrabold ${STATUS_STYLE[d.status] || "bg-slate-300"}`} data-testid="label-detail-status">
                    {STATUS_LABELS[d.status]}
                  </span>
                  {settings && (
                    <span className="rounded-full bg-cloud px-2.5 py-1 text-[10.5px] font-bold text-slate-500" data-testid="label-usps-mode">
                      {settings.usps_mode === "api" ? "USPS API" : settings.usps_mode === "returns_portal" ? "USPS Returns portal" : "Manual mode"}
                    </span>
                  )}
                  <span className="text-[12.5px] text-slate-400">{fmt(d.created_at)}</span>
                </div>
                <h3 className="font-display mt-3 text-xl font-extrabold text-ink">{d.practice_name}</h3>
              </div>
              <button onClick={onClose} className="rounded-xl p-2 text-slate-400 hover:bg-cloud hover:text-ink" data-testid="label-detail-close">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              <div className="rounded-2xl border border-ink/10 bg-paper p-4 text-[13.5px]">
                <p className="eyebrow text-slate-400">Contact</p>
                <p className="mt-2 font-bold text-ink">{d.first_name} {d.last_name}</p>
                <p className="mt-1">{d.email}</p>
                <p className="mt-0.5">{d.phone}</p>
              </div>
              <div className="rounded-2xl border border-ink/10 bg-paper p-4 text-[13.5px]">
                <p className="eyebrow text-slate-400">Ship label to (their office)</p>
                <p className="mt-2 font-bold text-ink">{d.street}{d.unit ? `, ${d.unit}` : ""}</p>
                <p>{d.city}, {d.state} {d.zip}</p>
                {d.address_validation?.validated && (
                  <p className="mt-1.5 text-[11.5px] font-bold text-brandleaf-deep">
                    USPS validated{d.address_validation.dpv_confirmation ? ` · DPV ${d.address_validation.dpv_confirmation}` : ""}
                  </p>
                )}
                {d.address_validation && !d.address_validation.validated && (
                  <p className="mt-1.5 text-[11.5px] font-bold text-amber-600">Not USPS-verified: {d.address_validation.reason}</p>
                )}
              </div>
              <div className="rounded-2xl border border-ink/10 bg-paper p-4 text-[13.5px]">
                <p className="eyebrow text-slate-400">Repair info</p>
                <p className="mt-2 font-bold text-ink">{d.handpiece_count} × {d.handpiece_type}</p>
                <p className="mt-0.5">{d.brand_model || "Brand not specified"}</p>
                <p className="mt-1 text-slate-600">{d.problem || "No problem description"}</p>
                {d.notes && <p className="mt-1 text-slate-500">Notes: {d.notes}</p>}
              </div>
              <div className="rounded-2xl border border-ink/10 bg-paper p-4 text-[13.5px]">
                <p className="eyebrow text-slate-400">Package</p>
                <p className="mt-2">{d.pkg_weight ? `${d.pkg_weight} lb` : "Weight: not given"}</p>
                <p className="mt-0.5">{d.pkg_dims ? `Dimensions: ${d.pkg_dims}` : "Dimensions: not given"}</p>
                <p className="mt-1">Label delivery: <strong>{d.delivery_method === "email" ? "Email" : "Download from confirmation page"}</strong></p>
              </div>
            </div>

            {d.label && (d.label.has_pdf || d.label.tracking || d.label.provider === "usps_returns_portal") && (
              <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-brandleaf/40 bg-brandleaf-soft p-4" data-testid="label-existing-card">
                <div className="text-[13.5px]">
                  <p className="font-bold text-ink">
                    Label on file ({d.label.provider === "usps_api" ? "USPS API" : d.label.provider === "usps_returns_portal" ? "USPS Returns portal" : "manual upload"})
                  </p>
                  <p className="mt-0.5">
                    {d.label.tracking ? <>Tracking: <span className="font-mono font-bold text-brandsky-deep">{d.label.tracking}</span> · </> : <span className="text-amber-700">Tracking not recorded yet</span>}
                    {d.label.cost ? `Cost: ${d.label.cost} · ` : ""}Created {fmt(d.label.created_at)}
                  </p>
                  {d.label.provider === "usps_returns_portal" && !d.label.tracking && (
                    <form onSubmit={async (e) => {
                      e.preventDefault();
                      await act({ action: "set_tracking", note: e.target.elements["rt-tracking"].value }, "rt", "Tracking number saved");
                    }} className="mt-2 flex items-center gap-2" data-testid="label-returns-tracking-form">
                      <input name="rt-tracking" className="field !w-52 !py-1.5 !text-[12px]" placeholder="Tracking # from their label" data-testid="label-returns-tracking-input" />
                      <button type="submit" className="rounded-full bg-ink px-4 py-1.5 text-[11.5px] font-bold text-white" data-testid="label-returns-tracking-save">Save</button>
                    </form>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {d.label.has_pdf && (
                    <button onClick={() => download(`/admin/label-requests/${d.request_id}/label.pdf`, `JSL-label-${d.request_id}.pdf`)} className="btn-lift inline-flex items-center gap-2 rounded-full border border-ink/15 bg-white px-4 py-2 text-[12px] font-bold" data-testid="label-download-pdf">
                      <Download className="h-3.5 w-3.5" /> PDF
                    </button>
                  )}
                  <button onClick={() => act({ action: "note", note: "label email resent via button" }, "resend", "Working…") || true} className="hidden" />
                  <button onClick={async () => {
                    setBusy("resend");
                    const res = await fetch(`${API}/admin/label-requests/${d.request_id}/resend-label-email`, { method: "POST", headers: { Authorization: `Bearer ${token}` } });
                    setBusy("");
                    if (res.ok) {
                      toast.success("Label email resent");
                      await load();
                    } else {
                      toast.error("Could not resend");
                    }
                  }} disabled={busy === "resend"} className="btn-lift inline-flex items-center gap-2 rounded-full bg-ink px-4 py-2 text-[12px] font-bold text-white" data-testid="label-resend-email">
                    {busy === "resend" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Resend email
                  </button>
                  <button onClick={async () => {
                    setBusy("track");
                    const res = await fetch(`${API}/admin/label-requests/${d.request_id}/check-tracking`, { method: "POST", headers: { Authorization: `Bearer ${token}` } });
                    const j = await res.json().catch(() => ({}));
                    setBusy("");
                    if (res.ok) {
                      toast.success(j.note || `USPS status: ${j.summary?.eventDescription || "checked"}`);
                      await load();
                    } else {
                      toast.error(j.detail || "Tracking check failed");
                    }
                  }} disabled={busy === "track"} className="btn-lift inline-flex items-center gap-2 rounded-full border border-ink/15 bg-white px-4 py-2 text-[12px] font-bold" data-testid="label-check-tracking">
                    {busy === "track" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Check tracking
                  </button>
                </div>
              </div>
            )}

            <div className="mt-5 rounded-2xl border border-ink/10 p-4" data-testid="label-shipping-editor">
              <p className="eyebrow text-slate-400">Shipping address (editable)</p>
              <div className="mt-3 grid gap-2.5 sm:grid-cols-5">
                {["street", "unit", "city", "state", "zip"].map((k) => (
                  <input
                    key={k}
                    className="field !py-2.5 !text-[13px]"
                    placeholder={k.toUpperCase()}
                    value={shipping?.[k] || ""}
                    onChange={(e) => setShipping((s) => ({ ...s, [k]: e.target.value }))}
                    data-testid={`label-edit-${k}`}
                  />
                ))}
              </div>
              <button
                onClick={() => act({ action: "shipping", shipping }, "save", "Shipping address corrected")}
                disabled={busy === "save"}
                className="mt-3 rounded-full border border-ink/15 px-5 py-2 text-[12px] font-bold text-ink hover:border-ink/40"
                data-testid="label-save-shipping"
              >
                Save address correction
              </button>
            </div>

            <div className="mt-5 flex flex-wrap gap-2" data-testid="label-actions">
              {!d.label && (d.status === "approved" || d.status === "label_failed") && settings?.returns_connected && (
                <button
                  onClick={async () => {
                    setBusy("returns");
                    const res = await fetch(`${API}/admin/label-requests/${d.request_id}/send-returns-link`, { method: "POST", headers: { Authorization: `Bearer ${token}` } });
                    const j = await res.json().catch(() => ({}));
                    setBusy("");
                    if (res.ok) {
                      toast.success("Prepaid-label link emailed to the office");
                      await load();
                    } else {
                      toast.error(j.detail || "Could not send label link");
                    }
                  }}
                  disabled={busy === "returns"}
                  className="btn-lift inline-flex items-center gap-2 rounded-full bg-brandpink px-5 py-2.5 text-[12.5px] font-bold text-white hover:bg-brandpink-deep disabled:opacity-50"
                  data-testid="label-send-returns-link"
                >
                  {busy === "returns" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  Email label link (USPS Returns)
                </button>
              )}

              {(d.status === "pending_approval" || d.status === "more_info" || d.status === "rejected") && (
                confirmApprove ? (
                  <span className="flex items-center gap-2 rounded-full bg-brandpink-soft px-4 py-2 text-[12px] font-bold text-ink">
                    {settings?.usps_mode === "api" ? "Purchase a prepaid USPS label now?" : "Approve this request?"}
                    <button onClick={async () => {
                      if (settings?.usps_mode === "api") {
                        await generateLabel();
                      } else {
                        await act({ action: "approve", note }, "approve", "Request approved");
                      }
                    }} disabled={busy === "generate" || busy === "approve"} className="rounded-full bg-ink px-4 py-1.5 text-[12px] font-bold text-white" data-testid="label-confirm-generate">
                      {(busy === "generate" || busy === "approve") ? "Working…" : "Confirm"}
                    </button>
                    <button onClick={() => setConfirmApprove(false)} className="text-slate-400 hover:text-ink">Cancel</button>
                  </span>
                ) : (
                  <button
                    onClick={() => setConfirmApprove(true)}
                    className="btn-lift inline-flex items-center gap-2 rounded-full bg-brandleaf-deep px-5 py-2.5 text-[12.5px] font-bold text-white hover:opacity-90"
                    data-testid="label-approve-button"
                  >
                    <FileCheck2 className="h-4 w-4" /> Approve request
                  </button>
                )
              )}
              {d.status === "pending_approval" && (
                <button onClick={() => act({ action: "more_info", note }, "info", "Info request emailed to office")} disabled={busy === "info" || !note.trim()}
                  className="btn-lift inline-flex items-center gap-2 rounded-full border border-amber-500/50 bg-amber-50 px-5 py-2.5 text-[12.5px] font-bold text-amber-700 disabled:opacity-50" data-testid="label-moreinfo-button">
                  <MessageSquareWarning className="h-4 w-4" /> Request more info
                </button>
              )}
              {d.status === "pending_approval" && (
                <button onClick={() => act({ action: "reject", note }, "reject", "Request declined + office emailed")} disabled={busy === "reject" || !note.trim()}
                  className="btn-lift inline-flex items-center gap-2 rounded-full border border-red-300 bg-red-50 px-5 py-2.5 text-[12.5px] font-bold text-red-700 disabled:opacity-50" data-testid="label-reject-button">
                  <Ban className="h-4 w-4" /> Decline
                </button>
              )}
              {!d.label && (d.status === "approved" || d.status === "label_failed") && (
                <form onSubmit={uploadLabel} className="flex w-full flex-wrap items-center gap-2 rounded-2xl border border-dashed border-brandsky/50 bg-brandsky-soft/40 p-3" data-testid="label-upload-form">
                  <input name="up-tracking" className="field !w-44 !py-2 !text-[13px]" placeholder="Tracking number" data-testid="label-upload-tracking" />
                  <input name="up-file" type="file" accept="application/pdf" className="field !w-56 !py-1.5 !text-[12px] file:mr-2 file:rounded-full file:border-0 file:bg-ink file:px-3 file:py-1.5 file:text-[11px] file:font-bold file:text-white" data-testid="label-upload-file" />
                  <button type="submit" disabled={busy === "upload"} className="btn-lift inline-flex items-center gap-2 rounded-full bg-ink px-5 py-2 text-[12.5px] font-bold text-white disabled:opacity-50" data-testid="label-upload-send">
                    {busy === "upload" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Send label
                  </button>
                  <span className="text-[11px] text-slate-500">Manual fallback: attach a PDF you purchased in your USPS business account.</span>
                </form>
              )}
              <select
                value={d.status}
                onChange={(e) => act({ action: "status", status: e.target.value }, "status", `Status set to ${STATUS_LABELS[e.target.value]}`)}
                className="field !w-auto !py-2 !text-[12.5px] font-bold"
                data-testid="label-status-select"
              >
                {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
              </select>
              {settings?.callcenter_url && (
                <a href={settings.callcenter_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 self-center text-[11.5px] font-bold text-brandsky-deep link-underline" data-testid="label-callcenter-link">
                  Open USPS Returns (Call Center) ↗
                </a>
              )}
              <span className="text-[11px] text-slate-400 self-center">Marking "Delivered" emails the office that their package arrived.</span>
            </div>

            <div className="mt-5">
              <p className="eyebrow text-slate-400">Admin note (internal + used for decline/info messages)</p>
              <textarea className="field mt-2 min-h-[70px] resize-y" placeholder="e.g. why you're declining, or what info you need…" value={note} onChange={(e) => setNote(e.target.value)} data-testid="label-admin-note" />
            </div>

            <div className="mt-5" data-testid="label-audit-history">
              <p className="eyebrow text-slate-400">Audit log</p>
              <ul className="mt-2 max-h-40 space-y-1.5 overflow-y-auto rounded-xl bg-paper p-3">
                {(d.audit || []).slice().reverse().map((a, i) => (
                  <li key={i} className="text-[12px] text-slate-600">
                    <span className="font-mono text-[10.5px] text-slate-400">{fmt(a.at)}</span>{" "}
                    <strong className="text-ink">{a.action}</strong> — {a.by}{a.note ? ` · ${a.note}` : ""}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const STATUS_GROUPS = [
  ["all", "All", null],
  ["unseen", "Not yet seen", ["pending_approval"]],
  ["todo", "To do", ["approved", "more_info", "label_in_progress", "label_generated", "label_sent", "shipped"]],
  ["done", "Completed", ["delivered", "cancelled"]],
  ["problem", "Problem", ["rejected", "label_failed"]],
];

export default function AdminLabels({ token }) {
  const [rows, setRows] = useState(null);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [openId, setOpenId] = useState(null);
  const [settings, setSettings] = useState(null);
  const [section, setSection] = useState("all");

  useEffect(() => {
    fetch(`${API}/admin/settings`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then(setSettings)
      .catch(() => {});
  }, [token]);

  const load = useCallback(async () => {
    const qs = new URLSearchParams();
    if (status) qs.set("status", status);
    if (q.trim()) qs.set("q", q.trim());
    const res = await fetch(`${API}/admin/label-requests?${qs.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
    if (res.status === 401) {
      toast.error("Session expired — sign in again.");
      return;
    }
    if (!res.ok) {
      toast.error("Could not load label requests");
      return;
    }
    const data = await res.json();
    setRows(data.requests);
  }, [token, status, q]);

  useEffect(() => {
    const t = setTimeout(load, q ? 350 : 0);
    return () => clearTimeout(t);
  }, [load, q]);

  const exportCsv = async () => {
    const res = await fetch(`${API}/admin/label-requests-export.csv`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) {
      toast.error("Export failed");
      return;
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "jsl-label-requests.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const deleteRow = async (rid) => {
    if (!window.confirm(`Delete label request ${rid}? This cannot be undone.`)) return;
    const res = await fetch(`${API}/admin/label-requests/${rid}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) {
      setRows((prev) => prev.filter((r) => r.request_id !== rid));
      toast.success("Request deleted");
    } else {
      toast.error("Could not delete");
    }
  };

  const secDef = STATUS_GROUPS.find(([id]) => id === section);
  const visible = rows ? rows.filter((r) => !secDef[2] || secDef[2].includes(r.status)) : [];

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
      <div className="mb-5 flex flex-wrap gap-2" data-testid="labels-section-tabs">
        {STATUS_GROUPS.map(([id, label, group]) => {
          const n = rows ? rows.filter((r) => !group || group.includes(r.status)).length : "…";
          return (
            <button
              key={id}
              onClick={() => setSection(id)}
              data-testid={`labels-section-${id}`}
              className={`rounded-full px-4 py-2 text-[12.5px] font-bold transition-all ${
                section === id ? "bg-ink text-white shadow-[0_8px_20px_rgba(22,50,79,0.25)]" : "border border-ink/15 bg-white text-ink hover:border-ink/40"
              }`}
            >
              {label} <span className="font-mono opacity-70">{n}</span>
            </button>
          );
        })}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-xl font-extrabold text-ink">Shipping-label requests</h2>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              className="field !w-64 !py-2.5 !pl-9 !text-[13px]"
              placeholder="Search office, email, or ID…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="labels-search-input"
            />
          </div>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="field !w-auto !py-2.5 !text-[13px] font-semibold" data-testid="labels-status-filter">
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
          </select>
          <button onClick={exportCsv} className="btn-lift inline-flex items-center gap-2 rounded-full border border-ink/15 bg-white px-4 py-2.5 text-[12.5px] font-bold text-ink hover:border-ink/40" data-testid="labels-export-csv">
            <Download className="h-3.5 w-3.5" /> CSV
          </button>
        </div>
      </div>

      {!rows ? (
        <p className="flex items-center gap-2 py-20 text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</p>
      ) : rows.length === 0 ? (
        <div className="mt-6 rounded-[24px] border border-dashed border-ink/20 py-20 text-center" data-testid="labels-empty">
          <p className="font-display text-lg font-bold text-ink">No label requests{status ? ` with status "${STATUS_LABELS[status]}"` : ""} yet</p>
          <p className="mt-1 text-[13.5px] text-slate-500">Requests from the nationwide page will appear here for approval.</p>
        </div>
      ) : (
        <div className="mt-6 overflow-x-auto rounded-[20px] border border-ink/10 bg-white shadow-[0_8px_30px_rgba(22,50,79,0.07)]" data-testid="labels-table">
          <table className="w-full min-w-[760px] text-left text-[13px]">
            <thead>
              <tr className="border-b border-ink/10 bg-cloud/60">
                {["Request", "Submitted", "Office", "Contact", "Handpieces", "Status", "Tracking", ""].map((h) => (
                  <th key={h} className="px-4 py-3 font-mono text-[10.5px] font-semibold uppercase tracking-wider text-slate-400">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.request_id} className="border-b border-ink/5 transition-colors last:border-0 hover:bg-brandsky-soft/40" data-testid={`label-row-${r.request_id}`}>
                  <td className="px-4 py-3 font-mono text-[12px] font-bold text-ink">{r.request_id}</td>
                  <td className="px-4 py-3 text-slate-500">{fmt(r.created_at)}</td>
                  <td className="px-4 py-3 font-bold text-ink">{r.practice_name}</td>
                  <td className="px-4 py-3">
                    <span className="block">{r.first_name} {r.last_name}</span>
                    <span className="block text-[11.5px] text-slate-400">{r.state} · {r.email}</span>
                  </td>
                  <td className="px-4 py-3">{r.handpiece_count} × {r.handpiece_type}</td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full px-2.5 py-1 text-[10.5px] font-extrabold ${STATUS_STYLE[r.status] || "bg-slate-300"}`}>{STATUS_LABELS[r.status]}</span>
                  </td>
                  <td className="px-4 py-3 font-mono text-[11.5px] text-brandsky-deep">{r.label?.tracking || "—"}</td>
                  <td className="px-4 py-3">
                    <button onClick={() => setOpenId(r.request_id)} className="inline-flex items-center gap-1 rounded-full bg-ink px-3.5 py-1.5 text-[11.5px] font-bold text-white hover:bg-abyss" data-testid={`label-open-${r.request_id}`}>
                      Open <ChevronRight className="h-3.5 w-3.5" />
                    </button>
                    <button onClick={() => deleteRow(r.request_id)} className="ml-1 inline-flex items-center rounded-full border border-ink/10 px-2.5 py-1.5 text-slate-400 transition-colors hover:border-red-300 hover:text-red-600" aria-label={`Delete request ${r.request_id}`} data-testid={`label-delete-${r.request_id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AnimatePresence>
        {openId && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <DetailModal rid={openId} token={token} onClose={() => setOpenId(null)} refresh={load} settings={settings} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}